import { useState } from "react";
import { ShieldQuestion, Plus, Trash2, Pencil, Search, ChevronLeft, ChevronRight } from "lucide-react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ClubSelect } from "@/components/ClubSelect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { API_ENDPOINTS } from "@/api/endpoints";
import { toast } from "react-toastify";
import { cn } from "@/lib/utils";

const LEAGUES = ["premier", "laliga", "seriea", "bundesliga", "ucl"] as const;
type League = typeof LEAGUES[number];

const LEAGUE_LABELS: Record<League, string> = {
  premier: "Premier League",
  laliga: "La Liga",
  seriea: "Serie A",
  bundesliga: "Bundesliga",
  ucl: "UCL / European",
};

const LEAGUE_COLORS: Record<League, string> = {
  premier: "bg-purple-100 text-purple-700",
  laliga: "bg-red-100 text-red-700",
  seriea: "bg-blue-100 text-blue-700",
  bundesliga: "bg-yellow-100 text-yellow-700",
  ucl: "bg-sky-100 text-sky-700",
};

const LEAGUE_FLAGS: Record<League, string> = {
  premier: "🏴󠁧󠁢󠁥󠁮󠁧󠁿", laliga: "🇪🇸", seriea: "🇮🇹", bundesliga: "🇩🇪", ucl: "🏆",
};

const CLUE_PLACEHOLDERS = [
  "League name, e.g. English Premier League",
  "Country, e.g. England",
  "Nickname, e.g. The Citizens",
  "Stadium, e.g. Etihad Stadium",
  "Founded year, e.g. Founded: 1880",
  "Kit colors, e.g. Sky Blue & White",
];

interface MysteryClub {
  id: number;
  clubId: number;
  imageUrl: string | null;
  league: string;
  clue1: string;
  clue2: string;
  clue3: string;
  clue4: string;
  clue5: string;
  clue6: string;
  order: number;
  isActive: boolean;
  club: {
    id: number;
    name: string;
    logoUrl: string | null;
  };
}

const PAGE_SIZE = 12;

const emptyForm = {
  clubId: 0,
  clubName: "",
  clubLogoUrl: "",
  league: "premier" as League,
  clue1: "",
  clue2: "",
  clue3: "",
  clue4: "",
  clue5: "",
  clue6: "",
  order: "" as string | number,
};
type FormState = typeof emptyForm;

export default function MysteryClubPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<MysteryClub | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<{ clubId?: string; clues?: string }>({});
  const [search, setSearch] = useState("");
  const [leagueFilter, setLeagueFilter] = useState<League | "all">("all");
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery<MysteryClub[]>({
    queryKey: ["quiz", "mystery-club"],
    queryFn: async () => {
      const r = await apiClient.get(API_ENDPOINTS.QUIZ.MYSTERY_LIST) as unknown as MysteryClub[] | null;
      return r ?? [];
    },
  });
  const clubs = data ?? [];

  const invalidate = () => qc.invalidateQueries({ queryKey: ["quiz", "mystery-club"] });

  const { mutate: create, isPending: isCreating } = useMutation({
    mutationFn: (d: object) => apiClient.post(API_ENDPOINTS.QUIZ.MYSTERY_CREATE, d),
    onSuccess: () => { toast.success("Club created"); invalidate(); closeDialog(); },
    onError: (e: any) => toast.error(e.response?.data?.message || "Failed to create"),
  });

  const { mutate: update, isPending: isUpdating } = useMutation({
    mutationFn: ({ id, d }: { id: number; d: object }) =>
      apiClient.patch(API_ENDPOINTS.QUIZ.MYSTERY_UPDATE(id), d),
    onSuccess: () => { toast.success("Updated"); invalidate(); closeDialog(); },
    onError: (e: any) => toast.error(e.response?.data?.message || "Failed to update"),
  });

  const { mutate: remove } = useMutation({
    mutationFn: (id: number) => apiClient.delete(API_ENDPOINTS.QUIZ.MYSTERY_DELETE(id)),
    onSuccess: () => { toast.success("Deleted"); invalidate(); },
    onError: (e: any) => toast.error(e.response?.data?.message || "Failed to delete"),
  });

  const { mutate: toggle } = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      apiClient.patch(API_ENDPOINTS.QUIZ.MYSTERY_UPDATE(id), { isActive }),
    onSuccess: () => invalidate(),
    onError: () => toast.error("Toggle failed"),
  });

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, order: clubs.length });
    setOpen(true);
  };

  const openEdit = (c: MysteryClub) => {
    setEditing(c);
    setForm({
      clubId: c.club.id,
      clubName: c.club.name,
      clubLogoUrl: c.club.logoUrl ?? "",
      league: c.league as League,
      clue1: c.clue1, clue2: c.clue2, clue3: c.clue3,
      clue4: c.clue4, clue5: c.clue5, clue6: c.clue6,
      order: c.order,
    });
    setOpen(true);
  };

  const closeDialog = () => { setOpen(false); setEditing(null); setErrors({}); };
  const set = (k: keyof FormState, v: any) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = () => {
    const newErrors: typeof errors = {};
    if (!form.clubId) newErrors.clubId = "Please select a club";
    const clues = [form.clue1, form.clue2, form.clue3, form.clue4, form.clue5, form.clue6];
    if (clues.some((c) => !c.trim())) newErrors.clues = "All 6 clues are required";
    if (Object.keys(newErrors).length) { setErrors(newErrors); return; }
    if (!editing) {
      const dup = clubs.find((c) => c.club.id === form.clubId && c.league === form.league);
      if (dup) { setErrors({ clubId: `${form.clubName} already exists as a Mystery Club in this league` }); return; }
    }
    setErrors({});
    const payload = {
      clubId: form.clubId,
      league: form.league,
      clue1: form.clue1.trim(),
      clue2: form.clue2.trim(),
      clue3: form.clue3.trim(),
      clue4: form.clue4.trim(),
      clue5: form.clue5.trim(),
      clue6: form.clue6.trim(),
      order: form.order !== "" ? Number(form.order) : clubs.length,
    };
    if (editing) update({ id: editing.id, d: payload });
    else create(payload);
  };

  const filtered = [...clubs]
    .sort((a, b) => a.order - b.order)
    .filter((c) => {
      if (leagueFilter !== "all" && c.league !== leagueFilter) return false;
      if (!search.trim()) return true;
      const s = search.toLowerCase();
      return c.club.name.toLowerCase().includes(s) || c.league.toLowerCase().includes(s);
    });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const activeCount = clubs.filter((c) => c.isActive).length;

  return (
    <Layout>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <ShieldQuestion className="h-8 w-8 text-emerald-500" />
            Mystery Club
          </h1>
          <p className="text-gray-500 mt-1">
            {clubs.length} clubs · {activeCount} active
          </p>
        </div>
        <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700 text-white">
          <Plus className="h-4 w-4 mr-2" />
          Add Club
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-5 gap-3 mb-5">
        {LEAGUES.map((l) => (
          <button
            key={l}
            onClick={() => { setLeagueFilter(leagueFilter === l ? "all" : l); setPage(1); }}
            className={cn(
              "bg-white rounded-xl border shadow-sm p-3 text-center transition-colors",
              leagueFilter === l && "ring-2 ring-emerald-500 border-emerald-300",
            )}
          >
            <p className="text-xl">{LEAGUE_FLAGS[l]}</p>
            <p className="text-lg font-bold text-gray-900">{clubs.filter((c) => c.league === l).length}</p>
            <p className="text-[11px] text-gray-500 mt-0.5">{LEAGUE_LABELS[l]}</p>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-xs mb-4">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search by club name…"
          className="pl-8 h-9 text-sm"
        />
      </div>

      {/* Cards */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-500" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <ShieldQuestion className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium">{search ? "No matches" : "No clubs yet"}</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {paged.map((club) => (
              <div
                key={club.id}
                className={cn(
                  "bg-white rounded-xl border shadow-sm p-4 flex flex-col gap-3",
                  !club.isActive && "opacity-60",
                )}
              >
                {/* Card header */}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2.5 min-w-0">
                    {club.club.logoUrl ? (
                      <img
                        src={club.club.logoUrl}
                        alt=""
                        className="w-9 h-9 rounded-full object-contain border border-gray-100 shrink-0"
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                        <span className="text-emerald-700 text-sm font-bold">
                          {club.club.name.charAt(0)}
                        </span>
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="font-bold text-gray-900 truncate">{club.club.name}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <Badge className={cn("text-[10px] px-1.5 py-0", LEAGUE_COLORS[club.league as League])}>
                          {LEAGUE_FLAGS[club.league as League]} {LEAGUE_LABELS[club.league as League]}
                        </Badge>
                        <span className="text-[10px] text-gray-400">#{club.order}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Switch
                      checked={club.isActive}
                      onCheckedChange={(v) => toggle({ id: club.id, isActive: v })}
                      className="data-[state=checked]:bg-green-500 data-[state=unchecked]:bg-red-400 scale-75"
                    />
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(club)}>
                      <Pencil className="h-3.5 w-3.5 text-gray-400" />
                    </Button>
                    <Button
                      variant="ghost" size="sm" className="h-7 w-7 p-0"
                      onClick={() => { if (confirm(`Delete ${club.club.name}?`)) remove(club.id); }}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-red-400" />
                    </Button>
                  </div>
                </div>

                {/* Clues */}
                <div className="space-y-1.5 border-t pt-3">
                  {([club.clue1, club.clue2, club.clue3, club.clue4, club.clue5, club.clue6] as string[]).map((clue, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 rounded px-1.5 py-0.5 shrink-0 mt-0.5 min-w-[20px] text-center">
                        {i + 1}
                      </span>
                      <p className="text-xs text-gray-600 leading-relaxed">{clue}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-5 text-sm text-gray-500">
              <span>
                {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)} of {filtered.length}
              </span>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={safePage === 1} onClick={() => setPage((p) => p - 1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((n) => n === 1 || n === totalPages || Math.abs(n - safePage) <= 1)
                  .reduce<(number | "…")[]>((acc, n, idx, arr) => {
                    if (idx > 0 && n - (arr[idx - 1] as number) > 1) acc.push("…");
                    acc.push(n);
                    return acc;
                  }, [])
                  .map((n, i) =>
                    n === "…" ? (
                      <span key={`e-${i}`} className="px-1">…</span>
                    ) : (
                      <Button
                        key={n}
                        variant={n === safePage ? "default" : "outline"}
                        size="sm"
                        className={cn("h-8 w-8 p-0", n === safePage && "bg-emerald-600 hover:bg-emerald-700 text-white")}
                        onClick={() => setPage(n as number)}
                      >
                        {n}
                      </Button>
                    )
                  )}
                <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={safePage === totalPages} onClick={() => setPage((p) => p + 1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit: ${editing.club.name}` : "New Mystery Club"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="mb-1 block text-sm">Club (the answer — hidden from players) <span className="text-red-500">*</span></Label>
              <ClubSelect
                value={form.clubId ? { name: form.clubName, logoUrl: form.clubLogoUrl } : null}
                onChange={(c) => { setForm((f) => ({ ...f, clubId: c?.id ?? 0, clubName: c?.name ?? "", clubLogoUrl: c?.logoUrl ?? "" })); if (c) setErrors((er) => ({ ...er, clubId: undefined })); }}
              />
              {errors.clubId && <p className="text-xs text-red-500 mt-1 font-medium">{errors.clubId}</p>}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-1 block text-sm">League</Label>
                <Select value={form.league} onValueChange={(v) => set("league", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LEAGUES.map((l) => (
                      <SelectItem key={l} value={l}>{LEAGUE_FLAGS[l]} {LEAGUE_LABELS[l]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1 block text-sm">Display Order</Label>
                <Input
                  type="number"
                  value={form.order}
                  onChange={(e) => set("order", e.target.value)}
                  placeholder="0"
                  min={0}
                />
              </div>
            </div>
            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold text-gray-700">
                  Clues <span className="text-gray-400 font-normal">(revealed one-by-one on each wrong guess)</span> <span className="text-red-500">*</span>
                </p>
              </div>
              {errors.clues && <p className="text-xs text-red-500 mb-2 font-medium">{errors.clues}</p>}
              {([1, 2, 3, 4, 5, 6] as const).map((n) => {
                const key = `clue${n}` as keyof FormState;
                const isEmpty = !(form[key] as string).trim();
                return (
                  <div key={n} className="mb-3">
                    <Label className="mb-1 flex items-center gap-1.5 text-xs text-gray-500">
                      <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 rounded px-1.5 py-0.5">
                        {n}
                      </span>
                      Clue {n}
                    </Label>
                    <Input
                      value={form[key] as string}
                      onChange={(e) => { set(key, e.target.value); if (e.target.value.trim()) setErrors((er) => ({ ...er, clues: undefined })); }}
                      placeholder={CLUE_PLACEHOLDERS[n - 1]}
                      className={errors.clues && isEmpty ? "border-red-400" : ""}
                    />
                  </div>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>Cancel</Button>
            <Button
              onClick={handleSave}
              disabled={isCreating || isUpdating}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {isCreating || isUpdating ? "Saving…" : editing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
